import type { StripeError } from "@stripe/stripe-js";

const AUTH_FAILURE_CODES = new Set([
	"payment_intent_authentication_failure",
	"setup_intent_authentication_failure",
	"authentication_required",
]);

const DECLINE_CODES = new Set([
	"card_declined",
	"generic_decline",
	"insufficient_funds",
	"lost_card",
	"stolen_card",
	"expired_card",
	"incorrect_cvc",
	"incorrect_number",
	"processing_error",
	"do_not_honor",
	"call_issuer",
	"not_permitted",
	"transaction_not_allowed",
]);

const VALIDATION_CODES = new Set([
	"incomplete_number",
	"incomplete_cvc",
	"incomplete_expiry",
	"incomplete_zip",
]);

const DEFAULT_PAYMENT_ERROR =
	"We couldn't complete your payment. Try a different card or payment method.";

const AUTH_FAILURE_MESSAGE =
	"We couldn't verify this payment method (bank authentication wasn't completed). Try again, or use a different card.";

const DECLINE_MESSAGE =
	"Your card was declined. Try a different card, or contact your bank if this keeps happening.";

const PAYMENT_METHOD_CUSTOMER_MISMATCH_CODE =
	"payment_method_customer_mismatch";

const PAYMENT_METHOD_CUSTOMER_MISMATCH_MESSAGE =
	"That card isn’t linked to this account. Enter a different card.";

const OPAQUE_SERVER_MESSAGES = new Set([
	"internal server error",
	"an error occurred while processing your request with stripe. please try again later. if the problem persists, contact support.",
]);

type PaymentErrorInput = {
	code?: string | null;
	declineCode?: string | null;
	message?: string | null;
	stripeError?: StripeError | null;
	httpStatus?: number | null;
};

type PaymentFailureSource = PaymentErrorInput & {
	/** Intent status or other fallback when Stripe omitted codes. */
	fallbackCode?: string | null;
};

type PaymentFailureDetails = {
	error_code: string;
	decline_code?: string;
	error_message: string;
	http_status?: number;
};

/**
 * Maps Stripe / API payment failures to actionable checkout copy.
 * Prefer the provider message when it's already clear; otherwise use a recovery hint.
 */
const getPaymentErrorMessage = ({
	code,
	declineCode,
	message,
	stripeError,
}: PaymentErrorInput = {}): string => {
	const resolvedCode =
		code || declineCode || stripeError?.code || stripeError?.decline_code;
	const resolvedMessage = message || stripeError?.message;
	const normalizedMessage = resolvedMessage?.trim().toLowerCase() ?? "";

	if (resolvedCode === PAYMENT_METHOD_CUSTOMER_MISMATCH_CODE) {
		return PAYMENT_METHOD_CUSTOMER_MISMATCH_MESSAGE;
	}

	if (resolvedCode && AUTH_FAILURE_CODES.has(resolvedCode)) {
		return resolvedMessage?.trim() || AUTH_FAILURE_MESSAGE;
	}

	if (resolvedCode && VALIDATION_CODES.has(resolvedCode)) {
		return resolvedMessage?.trim() || DEFAULT_PAYMENT_ERROR;
	}

	if (
		resolvedCode &&
		(DECLINE_CODES.has(resolvedCode) ||
			stripeError?.type === "card_error" ||
			declineCode ||
			stripeError?.decline_code)
	) {
		// Keep Stripe's specific decline reason when present (e.g. insufficient funds).
		if (
			resolvedMessage?.trim() &&
			!/^your card was declined\.?$/i.test(resolvedMessage.trim())
		) {
			return resolvedMessage.trim();
		}
		return DECLINE_MESSAGE;
	}

	if (
		resolvedMessage?.trim() &&
		!OPAQUE_SERVER_MESSAGES.has(normalizedMessage)
	) {
		return resolvedMessage.trim();
	}

	return DEFAULT_PAYMENT_ERROR;
};

const isRecoverablePaymentFailure = ({
	code,
	declineCode,
	stripeError,
}: PaymentErrorInput = {}): boolean => {
	const resolvedCode =
		code ||
		declineCode ||
		stripeError?.code ||
		stripeError?.decline_code ||
		undefined;

	if (!resolvedCode) {
		return stripeError?.type === "card_error";
	}

	return (
		resolvedCode === PAYMENT_METHOD_CUSTOMER_MISMATCH_CODE ||
		AUTH_FAILURE_CODES.has(resolvedCode) ||
		DECLINE_CODES.has(resolvedCode) ||
		VALIDATION_CODES.has(resolvedCode) ||
		stripeError?.type === "card_error"
	);
};

/**
 * Normalizes Stripe / API / intent last-error shapes into analytics + UI fields.
 * Always returns an `error_code` (falls back to `unknown`) so BAM payment_failed
 * events are filterable even when Stripe omitted codes.
 */
const getPaymentFailureDetails = (
	source: PaymentFailureSource = {},
): PaymentFailureDetails => {
	const errorCode = source.code || source.stripeError?.code || undefined;
	const declineCode =
		source.declineCode || source.stripeError?.decline_code || undefined;
	const error_code =
		errorCode || declineCode || source.fallbackCode || "unknown";
	const error_message = getPaymentErrorMessage({
		code: errorCode,
		declineCode,
		message: source.message,
		stripeError: source.stripeError,
	});
	const httpStatus = source.httpStatus;

	return {
		error_code,
		...(declineCode ? { decline_code: declineCode } : {}),
		error_message,
		...(typeof httpStatus === "number" ? { http_status: httpStatus } : {}),
	};
};

export {
	AUTH_FAILURE_MESSAGE,
	DECLINE_MESSAGE,
	DEFAULT_PAYMENT_ERROR,
	getPaymentErrorMessage,
	getPaymentFailureDetails,
	isRecoverablePaymentFailure,
};
export type { PaymentFailureDetails, PaymentFailureSource };
